import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Grid, Modal } from '@material-ui/core'
import { Form, Field } from 'react-final-form'
import ConfirmCard from '../../../components/common/Card'
import Text from '../../../components/common/Text'
import Button from '../../../components/common/Button'
import styled from '@emotion/styled'
import { composeValidators } from '../../../../utils/helper'
import { InputField } from '../../../components/fields'
import CategoryProductsSelect from './CategoryProductsSelect'
import SelectedProduct from './SelectedProduct'
import { EditCategoryProductItemType } from '.'
import { useGetGoldProducts } from '../../../services/sell/sell-query'
import { debounce } from 'lodash'

const InputFieldStyled = styled(InputField)`
  &.MuiFormControl-root {
    width: 100%;
    margin-top: 0;
  }
`

type Props = {
  open: boolean
  onClose: () => void
  initialValues?: EditCategoryProductItemType
  onSubmit: (value: EditCategoryProductItemType) => void
  isView?: boolean
}

const CreateOrEditModal = (props: Props) => {
  const { open, onClose, initialValues, onSubmit, isView = false } = props

  const validateRequired = useCallback((value: string) => (value ? undefined : 'required'), [])

  const validateRequiredAndMinNumber = useCallback((value: string) => {
    if (!value) {
      return 'required'
    }
    const numberValue = Number(value)
    return numberValue > 0 ? undefined : 'Must be greater than 0'
  }, [])

  const defaultSelectedProducts = useMemo(() => {
    return initialValues?.productIds || []
  }, [initialValues])

  const [selectedProducts, setSelectedProducts] = useState<string[]>(defaultSelectedProducts)

  const handleSetSelectedProducts = useCallback((products: string[]) => {
    setSelectedProducts(products)
  }, [])

  const [formCategoryKey, setFormCategoryName] = useState<string>(initialValues?.categoryName || '')

  const { data: products = [], isLoading, isFetching } = useGetGoldProducts(
    {
      tag: formCategoryKey,
      sortWeight: 'ASC',
      sortMintingCost: 'ASC',
      inStock: 'Y',
    },
    {
      enabled: !!formCategoryKey && !isView,
    },
  )

  const [hasAutoSelected, setHasAutoSelected] = useState(false)

  const debouncedSetCategoryName = useCallback(
    debounce((val: string) => {
      setFormCategoryName(val)
      if (!hasAutoSelected) {
        setHasAutoSelected(true)
      }
    }, 1000),
    [],
  )

  useEffect(() => {
    if (hasAutoSelected) {
      setSelectedProducts(products.slice(0, 6).map(product => String(product.id)))
    }
  }, [hasAutoSelected, products])

  return (
    <Modal
      open={open}
      onClose={onClose}
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <ConfirmCard>
        <Form<EditCategoryProductItemType>
          onSubmit={value => {
            const { name, categoryName, limit } = value
            onSubmit({
              id: initialValues?.id ?? '',
              rank: initialValues?.rank ?? 0,
              name: name.trim(),
              categoryName: categoryName?.trim(),
              limit: Number(limit),
              productIds: selectedProducts,
            })
          }}
          initialValues={initialValues}
        >
          {({ handleSubmit, values, errors, form }) => {
            return (
              <form onSubmit={handleSubmit}>
                <Grid container spacing={2} style={{ marginBottom: 8 }}>
                  <Grid sm={12} item>
                    <Text>Name</Text>
                    <Field
                      name="name"
                      component={InputFieldStyled}
                      validate={composeValidators(validateRequired)}
                      inputProps={{ maxLength: 13 }}
                      disabled={isView}
                    />
                  </Grid>

                  <Grid sm={12} item>
                    <Text>Max Product To Show</Text>
                    <Field
                      name="limit"
                      component={InputFieldStyled}
                      validate={composeValidators(validateRequiredAndMinNumber)}
                      disabled={isView}
                      type="number"
                    />
                  </Grid>

                  <Grid sm={12} item>
                    <Text>Category Key</Text>
                    <Field
                      name="categoryName"
                      component={InputFieldStyled}
                      validate={composeValidators(validateRequired)}
                      disabled={isView}
                      onChange={(e: { target: { value: any } }) => {
                        const value = e.target.value
                        debouncedSetCategoryName(value)
                        form.change('categoryName', value)
                      }}
                    />
                  </Grid>
                </Grid>

                {isView ? (
                  <SelectedProduct
                    selectedProducts={initialValues?.productIds}
                    categoryName={initialValues?.categoryName}
                  />
                ) : (
                  <CategoryProductsSelect
                    selectedProducts={selectedProducts}
                    onSetSelectedProducts={handleSetSelectedProducts}
                    isLoading={isLoading || isFetching}
                    products={products}
                  />
                )}

                {!isView ? (
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 20 }}>
                    <Button
                      type="submit"
                      disabled={
                        isLoading ||
                        isFetching ||
                        (!errors && Object.keys(errors).length > 0 && selectedProducts.length > 0)
                      }
                    >
                      Confirm
                    </Button>
                    <Button variant="danger" onClick={onClose}>
                      Cancel
                    </Button>
                  </div>
                ) : (
                  <div style={{ marginTop: 20, display: 'flex', justifyContent: 'center' }}>
                    <Button variant="primary" onClick={onClose}>
                      Close
                    </Button>
                  </div>
                )}
              </form>
            )
          }}
        </Form>
      </ConfirmCard>
    </Modal>
  )
}

export default CreateOrEditModal
